import { useMemo } from 'react';
import { DropdownItemProps } from 'semantic-ui-react';
import orderBy from 'lodash/orderBy';
import deburr from 'lodash/deburr';
import { InterventionLanguage } from '../types';
import { InterventionLanguagesApi } from '../api';
import { ApiError, defaultErrorHandler } from 'api';
import { useQuery } from 'react-query';

export interface InterventionLanguageLists {
  interventionLanguages: InterventionLanguage[];
  dropdownItems: DropdownItemProps[];
  indexedInterventionLanguages: Record<number, InterventionLanguage>;
  isFetched: boolean;
  isFetching: boolean;
}

export const useFetchInterventionLanguages = (options?: {
  staleTime?: number;
}): InterventionLanguageLists => {
  const {
    data: interventionLanguages,
    isFetching,
    isFetched,
  } = useQuery('interventionLanguages', InterventionLanguagesApi.getAll, {
    staleTime: 15 * 60 * 1000,
    onSettled: (_, error) =>
      error && error instanceof ApiError && defaultErrorHandler(error),
    ...options,
  });

  const indexedInterventionLanguages = useMemo(
    () =>
      (interventionLanguages || []).reduce((result, item) => {
        result[item.id] = item;
        return result;
      }, {} as Record<number, InterventionLanguage>),
    [interventionLanguages]
  );

  const dropdownItems = useMemo(() => {
    const activeInterventionLanguages = (interventionLanguages || []).filter(
      (il) => il.isActive
    );

    const items = activeInterventionLanguages.map((interventionLanguage) => ({
      key: interventionLanguage.id,
      value: interventionLanguage.id,
      text: interventionLanguage.name,
    }));

    const sortedItems = orderBy(items, (item) =>
      deburr(item.text).toLowerCase()
    );

    return sortedItems;
  }, [interventionLanguages]);

  return {
    interventionLanguages: interventionLanguages || [],
    dropdownItems,
    indexedInterventionLanguages,
    isFetched,
    isFetching,
  };
};
